import { observable, computed, action } from 'mobx'
import { has } from 'lodash'

import parseTreePath from 'utils/parseTreePath'
import createError from 'utils/createError'

import SessionStore from './SessionStore'
import Session from './Session'

type IDirection = 'up' | 'down'
interface IMoveOptions {
  wrap?: boolean
}

export interface ISessionSelectionTreeCreatePayload {
  sessionId: string
  isEditing: boolean
}

class SessionSelectionTree {
  @observable sessionId: string

  @observable isEditing: boolean

  @observable activePath: string // Paths look like -> [2]::playlist::artist::[0]::name
  @observable expandedPaths: string[]
  @observable selectionOrder: string[] = []

  constructor(selection: ISessionSelectionTreeCreatePayload) {
    this.sessionId = selection.sessionId
    this.isEditing = selection.isEditing

    this.activePath = ''
    this.expandedPaths = []
  }

  @computed
  private get session(): Session | null {
    return SessionStore.get(this.sessionId)
  }

  isExpanded = (path: string) => {
    return this.expandedPaths.includes(path)
  }

  /**
   * Select a very specific path
   *
   * @param path string - Path that is to be selected
   */
  @action
  select = (path: string) => {
    this.activePath = path
  }

  /**
   * Convenience wrapper around select, to move selection in ONE direction by some distance
   *
   * @param distance number - Distance to move selection
   * @param direction IDirection - Direction to move selection in
   */
  @action
  move = (distance: number, direction: IDirection) => {
    if (this.selectionOrder.length === 0) {
      // Should never happen, just in case
      this.setSelectionOrder()
    }

    const activePathIdx = this.selectionOrder.findIndex(
      o => o === this.activePath
    )
    let nextActivePath

    switch (direction) {
      case 'up':
        nextActivePath = this.selectionOrder[activePathIdx - distance]
        break
      case 'down':
        nextActivePath = this.selectionOrder[activePathIdx + distance]
        break
    }

    this.activePath = nextActivePath || '[0]'
  }

  /**
   * Expand the currently active path
   */
  @action
  expand = () => {
    if (this.isExpanded(this.activePath)) {
      // Already expanded, nothing to do
      return
    }

    if (!this.session?.script) {
      throw createError(
        'SessionSelectionTree.expand',
        'Unable to resolve recordIds',
        {
          sessionId: this.sessionId,
          scriptId: this.session?.scriptId,
        }
      )
    }

    const {
      record: recordAtPath,
      value: valueAtPath,
      field: fieldAtPath,
    } = parseTreePath(this.activePath, this.session.script.recordIds)

    if (valueAtPath === null || valueAtPath === undefined) {
      // `null` relations cannot be expanded
      return
    }

    if (
      !fieldAtPath?.isList &&
      (fieldAtPath?.isScalar || fieldAtPath?.isEnum)
    ) {
      // Scalars and enums are not expandable
      return
    }

    if (valueAtPath?.length === 0) {
      // Empty arrays cannot be expanded
      return
    }

    this.expandedPaths.push(this.activePath)
    this.setSelectionOrder()

    if (recordAtPath) {
      recordAtPath.fetchRelations() // No need to `await`, it'll finish when it finishes
    }
  }

  /**
   * Collapse the currently active path
   */
  @action
  collapse = () => {
    this.expandedPaths = this.expandedPaths.filter(
      path => !path.startsWith(this.activePath)
    )
    this.setSelectionOrder()
  }

  /**
   * Moves from an active path to its parent
   */
  @action
  jumpToParent() {
    const parentPath = this.activePath.split('::').slice(0, -1).join('::')
    if (parentPath == '') {
      return
    }

    this.activePath = parentPath
  }

  @action
  setSelectionOrder() {
    if (!this.session?.script) {
      throw createError(
        'SessionSelectionTree.setSelectionOrder',
        'Unable to resolve recordIds',
        {
          sessionId: this.sessionId,
          scriptId: this.session?.scriptId,
        }
      )
    }

    const { recordIds } = this.session.script
    let selectionOrder = recordIds.map((_, idx) => `[${idx}]`)

    this.expandedPaths.forEach(path => {
      const { value: valueAtPath, model: modelAtPath } = parseTreePath(
        path,
        recordIds
      )

      const idx = selectionOrder.findIndex(p => p === path)
      const pathsToInsert = []

      // First, add the path we're expanding. This will serve as the `meta` section for the selection order
      pathsToInsert.push(selectionOrder[idx])

      // Then, add this path's children so that they can be added to the selection order
      if (Array.isArray(valueAtPath)) {
        // If the value at this path is an array, add one element for each value in that array.

        // If the array has less than 100 items, then it is straightforward
        if (valueAtPath.length <= 100) {
          pathsToInsert.push(
            ...Array.from({ length: valueAtPath.length }).map(
              (_, i) => `${path}::[${i}]`
            )
          )
        } else {
          // Otherwise, split the value into batches of 100 items, and insert those paths
          const batchCount = Math.floor(valueAtPath.length / 100)
          pathsToInsert.push(
            ...Array.from({ length: batchCount }).map(
              (_, idx) => `${path}::[${100 * idx}-${100 * (idx + 1) - 1}]`
            )
          )
          if (valueAtPath.length % 100 !== 0) {
            pathsToInsert.push(
              `${path}::[${100 * batchCount}-${
                100 * batchCount + (valueAtPath.length % 100) - 1
              }]`
            )
          }
        }
      } else if (valueAtPath !== null) {
        // Otherwise add one element for each field of the model at this path
        if (modelAtPath) {
          pathsToInsert.push(
            ...modelAtPath.fields.map(f => `${path}::${f.name}`)
          )
        }
      }

      selectionOrder = [
        ...selectionOrder.slice(0, idx),
        ...pathsToInsert,
        ...selectionOrder.slice(idx + 1),
      ]
    })

    this.selectionOrder = selectionOrder
  }

  @action
  update = (selection: Partial<ISessionSelectionTreeCreatePayload>) => {
    has(selection, 'isEditing') && (this.isEditing = selection.isEditing!)
  }

  @action
  reset = () => {
    this.isEditing = false
  }
}

export default SessionSelectionTree
